/* Mobile quick view: bounded prefetch, current-market content and one-time assets. */
(() => {
  if (customElements.get('quick-view-modal')) return;
  const cache = new Map(), sections = new Map(), assets = new Map();
  const TTL = 120000, START = '<!--sn-quick-view:start-->', END = '<!--sn-quick-view:end-->';
  const storageKey = 'sn-qv-sections:v1:' + (document.currentScript?.src || location.pathname);
  let suppressWarm = false;
  // Only section IDs survive navigation. Prices/HTML never enter browser storage.
  try {
    for (const [key, value] of JSON.parse(sessionStorage.getItem(storageKey) || '[]')) {
      if (typeof key === 'string' && /^template--[\w-]+__main$/.test(value)) sections.set(key, value);
    }
  } catch (_) { /* Private browsing/storage restrictions are supported. */ }
  function saveSections() {
    try { sessionStorage.setItem(storageKey, JSON.stringify([...sections].slice(-24))); } catch (_) { /* optional */ }
  }
  function infoFor(button) {
    const url = new URL((button.dataset.productUrl || '').trim(), location.origin);
    if (url.origin !== location.origin || !/\/products\//.test(url.pathname)) throw new Error('Invalid product URL');
    ['section_id', 'sections', 'view'].forEach((key) => url.searchParams.delete(key));
    const market = window.SNMarket?.state;
    if (market?.country || window.Shopify?.country) url.searchParams.set('country', market?.country || window.Shopify.country);
    if (market?.language || window.Shopify?.locale) url.searchParams.set('language', market?.language || window.Shopify.locale);
    return { url, id: button.dataset.productId, market,
      key: url.href + '|' + (window.SNMarket?.key || ''),
      template: button.dataset.productTemplate ? [button.dataset.productTemplate, window.SNMarket?.key].join('|') : '' };
  }
  function valid(entry) { return entry && !entry.controller.signal.aborted && Date.now() - entry.created < TTL; }
  async function productText(response) {
    if (!response.body?.getReader || typeof TextDecoder === 'undefined') return response.text();
    const reader = response.body.getReader(), decoder = new TextDecoder();
    let text = '', found = false;
    try {
      while (true) {
        const { value, done } = await reader.read();
        text += decoder.decode(value, { stream: !done });
        if (!found) {
          const start = text.indexOf(START);
          if (start !== -1) { text = text.slice(start + START.length); found = true; }
        }
        const end = found ? text.indexOf(END) : -1;
        if (end !== -1) {
          // Stop before recommendations/footer/app markup. Cancellation is best effort.
          reader.cancel().catch(() => {});
          return text.slice(0, end);
        }
        if (done) return text; // Also supports other/older product templates without markers.
      }
    } finally { reader.releaseLock(); }
  }
  function scriptOnce(source) {
    const url = new URL(source.src, location.href).href;
    if (assets.has(url)) return assets.get(url);
    const existing = [...document.scripts].find((node) => node.src === url);
    if (existing && document.readyState === 'complete') return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const script = existing || document.createElement('script');
      const finish = (error) => {
        clearTimeout(timer); script.removeEventListener('load', onLoad); script.removeEventListener('error', onError);
        if (error) { if (!existing) script.remove(); reject(error); } else resolve();
      };
      const onLoad = () => finish(), onError = () => finish(new Error('Quick view asset failed'));
      const timer = setTimeout(onError, 10000);
      script.addEventListener('load', onLoad, { once: true }); script.addEventListener('error', onError, { once: true });
      if (!existing) {
        for (const attr of source.attributes) script.setAttribute(attr.name, attr.value);
        script.async = false;
        document.head.appendChild(script);
      }
    }).catch((error) => { assets.delete(url); throw error; });
    assets.set(url, promise);
    return promise;
  }
  function prepare(product) {
    const oldId = product.dataset.section;
    if (!oldId) throw new Error('Product section missing');
    const newId = 'quickview-' + oldId;
    product.innerHTML = product.innerHTML.split(oldId).join(newId);
    for (const attr of [...product.attributes]) {
      if (attr.value.includes(oldId)) product.setAttribute(attr.name, attr.value.split(oldId).join(newId));
    }
    product.dataset.originalSection = oldId; product.dataset.updateUrl = 'false';
    product.querySelectorAll('pickup-availability, share-button, product-modal, modal-dialog, side-drawer-opener:not(.agree-condition-popup-modal__opener--keep), side-drawer:not(.agree-condition-popup-modal__drawer--keep)').forEach((node) => node.remove());
    product.querySelector('[id^="Slider-Gallery"]')?.setAttribute('role', 'presentation');
    product.querySelectorAll('[id^="Slide-"]').forEach((node) => node.setAttribute('role', 'presentation'));
    // Keep zoom, but prioritize just the first gallery image on small screens.
    const images = [...product.querySelectorAll('media-gallery img')];
    const primary = images.find((img) => !img.closest('zoom-dialog, .swiper-thumbs, .thumbnail-list'));
    for (const img of images) {
      img.setAttribute('decoding', 'async'); img.setAttribute('loading', img === primary ? 'eager' : 'lazy');
      img.setAttribute('fetchpriority', img === primary ? 'high' : 'low');
      if (img.classList.contains('product-media__image') && !img.closest('zoom-dialog')) {
        img.sizes = '(max-width: 749px) calc(100vw - 48px), (max-width: 1199px) 45vw, 560px';
      }
    }
    // Pickup was removed above; recommendations are kept only if actually present.
    for (const script of product.querySelectorAll('script[src]')) {
      if (/\/pickup-availability\.js(?:\?|$)/.test(script.src) ||
          (/\/product-recommendations\.js(?:\?|$)/.test(script.src) && !product.querySelector('product-recommendations'))) script.remove();
    }
    return product;
  }
  function load(info) {
    let entry = cache.get(info.key);
    if (valid(entry)) return entry;
    entry?.controller.abort();
    const controller = new AbortController();
    entry = { controller, created: Date.now(), complete: false };
    const timer = setTimeout(() => controller.abort(), 15000);
    async function read(url) {
      const response = await fetch(url, { cache: 'no-store', signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Product HTTP ' + response.status);
      const product = new DOMParser().parseFromString(await productText(response), 'text/html').querySelector('product-info');
      if (!product || (info.id && info.id !== product.dataset.productId)) throw new Error('Product content missing or mismatched');
      if (info.market && ['country', 'currency', 'language', 'market'].some((key) =>
        product.dataset['qv' + key[0].toUpperCase() + key.slice(1)] !== undefined &&
        product.dataset['qv' + key[0].toUpperCase() + key.slice(1)] !== String(info.market[key] ?? ''))) {
        throw new Error('Product market mismatch');
      }
      return product;
    }
    entry.promise = (async () => {
      let product;
      const section = sections.get(info.template);
      if (section) {
        const url = new URL(info.url); url.searchParams.set('section_id', section);
        try { product = await read(url); }
        catch (error) { if (controller.signal.aborted) throw error; sections.delete(info.template); saveSections(); }
      }
      // Discover the actual section, preserving merchant blocks and their translations.
      if (!product) product = await read(info.url);
      if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
      if (info.template && product.dataset.section) { sections.set(info.template, product.dataset.section); saveSections(); }
      prepare(product);
      entry.scripts = [...product.querySelectorAll('script[src]')];
      entry.scripts.forEach((script) => script.remove());
      entry.product = product;
      entry.complete = true;
      return entry;
    })().catch((error) => { if (cache.get(info.key) === entry) cache.delete(info.key); throw error; }).finally(() => clearTimeout(timer));
    cache.set(info.key, entry);
    if (cache.size > 6) {
      const key = cache.keys().next().value;
      cache.get(key)?.controller.abort(); cache.delete(key);
    }
    return entry;
  }
  class QuickViewModal extends ModalDialog {
    constructor() {
      super(); this.modalContent = this.querySelector('#QuickViewModal'); this.generation = 0;
      this.addEventListener('product-info:loaded', ({ target }) => target.addPreProcessCallback?.(prepare));
      // A changed selection/submitted cart should reopen with freshly validated content.
      for (const event of ['change', 'submit']) this.addEventListener(event, () => this.invalidateRendered());
    }
    invalidateRendered() {
      if (this.rendered) cache.delete(this.rendered.key);
      this.rendered = null;
    }
    clearContent() {
      this.modalContent.querySelector('product-info')?.abortController?.abort();
      this.rendered = null; this.modalContent.replaceChildren(); this.modalContent.style.visibility = ''; clearTimeout(this.expiryTimer);
    }
    busy(button, active) {
      if (!button) return;
      button.classList.toggle('loading', active);
      if (active) button.setAttribute('aria-disabled', 'true'); else button.removeAttribute('aria-disabled');
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !active);
    }
    cancel() {
      if (this.request && !this.request.complete) this.request.controller.abort();
      this.busy(this.activeOpener, false); this.request = null;
    }
    focusDialog() {
      const dialog = this.querySelector('[role="dialog"]');
      if (dialog && typeof trapFocus === 'function') trapFocus(dialog, this.querySelector('.quick-view-modal__toggle'));
    }
    trapFocus() { this.focusDialog(); } // No accumulated transitionend listeners on repeated opens.
    async show(button) {
      if (!button || !this.modalContent) return;
      if (this.hasAttribute('open') && this.activeOpener === button && this.request && !this.request.complete && !this.request.controller.signal.aborted) return;
      this.cancel(); const run = ++this.generation; this.activeOpener = button;
      clearTimeout(this.expiryTimer); this.busy(button, true);
      let info;
      try { info = infoFor(button); } catch (error) { super.show(button); this.error(error, button, run); return; }
      const reuse = this.rendered?.key === info.key && valid(this.rendered.entry);
      if (!reuse) {
        this.clearContent();
        this.modalContent.replaceChildren(this.querySelector('[data-sn-quick-view-loading]').content.cloneNode(true));
      }
      this.modalContent.setAttribute('aria-busy', 'true'); this.modalContent.inert = true;
      if (reuse) this.modalContent.style.visibility = 'hidden';
      // Paint the shell now; market verification and product transfer run together.
      super.show(button);
      try {
        this.request = reuse ? this.rendered.entry : load(info);
        const [entry, ready] = await Promise.all([this.request.promise, window.SNMarket?.ready?.()]);
        if (ready === false) { if (run === this.generation) this.hide(); return; }
        if (run !== this.generation || !this.hasAttribute('open')) return;
        if (infoFor(button).key !== info.key) { this.show(button); return; }
        if (!reuse) {
          await Promise.all(entry.scripts.map(scriptOnce));
          if (await window.SNMarket?.ready?.() === false) { if (run === this.generation) this.hide(); return; }
          if (run !== this.generation || !this.hasAttribute('open')) return;
          if (infoFor(button).key !== info.key) { this.show(button); return; }
          const product = entry.product.cloneNode(true);
          [...this.modalContent.classList].filter((name) => name.startsWith('color-') || name === 'gradient').forEach((name) => this.modalContent.classList.remove(name));
          product.classList.forEach((name) => { if (name.startsWith('color-') || name === 'gradient') this.modalContent.classList.add(name); });
          // Remove executable inline scripts before adoption: cloning can reset their
          // parser flags. Run each exactly once after the complete product is attached.
          const inline = [];
          for (const old of product.querySelectorAll('script:not([src])')) {
            if (old.type && !/^(text|application)\/(java|ecma)script$/.test(old.type) && old.type !== 'module') continue;
            const anchor = product.ownerDocument.createComment('quick-view-script');
            old.replaceWith(anchor); inline.push({ old, anchor });
          }
          this.modalContent.replaceChildren(product);
          for (const { old, anchor } of inline) {
            const script = document.createElement('script');
            for (const attr of old.attributes) script.setAttribute(attr.name, attr.value);
            script.textContent = old.textContent; anchor.replaceWith(script);
          }
          this.rendered = { key: info.key, entry, enhanced: false };
        }
        if (!this.rendered.enhanced) {
          const rendered = this.rendered;
          // Give the product and controls a paint before optional enhancements.
          requestAnimationFrame(() => setTimeout(() => {
            if (run !== this.generation || !this.hasAttribute('open') || this.rendered !== rendered) return;
            rendered.enhanced = true;
            for (const enhance of [() => window.initProductSubtotals?.(this.modalContent),
              () => window.SNProductTrustStrip?.init(this.modalContent),
              () => window.Shopify?.PaymentButton?.init(), () => window.ProductModel?.loadShopifyXR()]) {
              try { enhance(); } catch (error) { console.warn('[SN quick view enhancement]', error); }
            }
          }, 0));
        }
        this.focusDialog();
      } catch (error) {
        if (run === this.generation) { cache.delete(info.key); this.error(error, button, run); }
      } finally {
        if (run === this.generation) {
          this.busy(button, false); this.modalContent.removeAttribute('aria-busy'); this.modalContent.inert = false; this.modalContent.style.visibility = '';
        }
      }
    }
    error(error, button, run) {
      if (run !== this.generation || !this.hasAttribute('open')) return;
      console.warn('[SN quick view]', error.name, error.message);
      this.request = null; this.clearContent(); this.busy(button, false); this.modalContent.removeAttribute('aria-busy'); this.modalContent.inert = false;
      this.modalContent.replaceChildren(this.querySelector('[data-sn-quick-view-error]').content.cloneNode(true));
      this.modalContent.querySelector('[data-sn-quick-view-retry]').onclick = () => { this.request = null; this.show(button); };
      const link = this.modalContent.querySelector('[data-sn-quick-view-details]');
      try { link.href = infoFor(button).url.href; } catch (_) { link.remove(); }
      this.focusDialog();
    }
    hide(preventFocus = false) {
      suppressWarm = true; ++this.generation; this.cancel();
      if (preventFocus === true) this.openedBy = null;
      try { document.querySelector('cart-drawer')?.setActiveElement?.(this.openedBy); }
      catch (error) { console.warn('[SN quick view close]', error); }
      try { super.hide(); }
      catch (error) { console.warn('[SN quick view cleanup]', error); }
      finally {
        this.removeAttribute('open'); this.modalContent.removeAttribute('aria-busy'); this.modalContent.inert = false;
        if (!this.rendered || !valid(this.rendered.entry)) this.clearContent();
        else this.expiryTimer = setTimeout(() => { if (!this.hasAttribute('open')) this.clearContent(); }, Math.max(0, TTL - (Date.now() - this.rendered.entry.created)));
        document.body.classList.remove('overflow-hidden'); document.documentElement.removeAttribute('scroll-lock');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
        queueMicrotask(() => { suppressWarm = false; });
      }
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);
  const cardSelector = '.card-wrapper, .card-product, .product-card, [data-sn-product-id]';
  const canWarm = () => !suppressWarm && !document.hidden && !navigator.connection?.saveData && !/^(slow-)?2g$/.test(navigator.connection?.effectiveType || '');
  let hoverTimer, warmBudget = 4;
  async function warmButton(button) {
    if (!button?.isConnected || !canWarm() || warmBudget <= 0 || document.querySelector('quick-view-modal[open]')) return;
    if ([...cache.values()].filter((entry) => !entry.complete).length >= 1) return;
    try {
      if (await window.SNMarket?.ready?.() === false || !canWarm() || !button.isConnected) return;
      const info = infoFor(button);
      if (valid(cache.get(info.key)) || [...cache.values()].some((entry) => !entry.complete)) return;
      --warmBudget;
      const entry = await load(info).promise;
      // Core assets are shared across products and loaded at most once.
      if (canWarm()) await Promise.all(entry.scripts.map(scriptOnce));
    } catch (_) { /* An intent/prefetch failure never interrupts browsing. */ }
  }
  const warm = (event) => {
    if (!canWarm() || !(event.target instanceof Element)) return;
    const card = event.target.closest(cardSelector);
    const button = event.target.closest('[data-open-quick-view-popup]') || card?.querySelector('[data-open-quick-view-popup]');
    if (!button || (event.relatedTarget instanceof Node && (card || button).contains(event.relatedTarget))) return;
    clearTimeout(hoverTimer);
    if (event.type === 'pointerover') {
      if (event.pointerType === 'touch') return;
      hoverTimer = setTimeout(() => warmButton(button), 120);
    } else warmButton(button);
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('pointerout', (event) => {
    const card = event.target.closest?.(cardSelector);
    if (!card || !(event.relatedTarget instanceof Node) || !card.contains(event.relatedTarget)) clearTimeout(hoverTimer);
  }, { passive: true });
  document.addEventListener('focusin', warm);
  document.addEventListener('touchstart', warm, { passive: true });
  // One visible product, after load and only on touch screens. Never walk the whole grid.
  const idleWarm = () => {
    if (!window.matchMedia?.('(pointer: coarse)').matches || !canWarm()) return;
    const start = () => {
      const button = [...document.querySelectorAll('[data-open-quick-view-popup]')].find((node) => {
        const rect = (node.closest(cardSelector) || node).getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight;
      });
      warmButton(button);
    };
    setTimeout(() => window.requestIdleCallback ? requestIdleCallback(start) : start(), 800);
  };
  if (document.readyState === 'complete') idleWarm(); else window.addEventListener('load', idleWarm, { once: true });
  function invalidate() {
    cache.forEach((entry) => entry.controller.abort()); cache.clear();
    const modal = document.querySelector('quick-view-modal'); modal?.invalidateRendered();
    return modal;
  }
  document.addEventListener('sn:market-change', () => {
    const modal = invalidate();
    if (modal?.hasAttribute('open') && modal.activeOpener) modal.show(modal.activeOpener); else modal?.clearContent();
  });
  document.addEventListener('sn:market-invalidated', () => {
    const modal = invalidate(); sections.clear(); saveSections();
    if (modal?.hasAttribute('open')) modal.hide(); else modal?.clearContent();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      const modal = invalidate();
      if (modal?.hasAttribute('open')) modal.hide(); else modal?.clearContent();
    }
  });
})();
